import React from 'react';
import { Dialog, Box, Stack, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Editorial detail modal — opens when the user clicks a schedule row or an
 * event card. Same visual language as the rest of the landing page (navy +
 * teal, Kufam + Cardo, hairline rules). Reusable for any item.
 *
 * Props:
 *   open         boolean
 *   onClose      () => void
 *   accent       optional css color (defaults to teal)
 *   kicker       small uppercase eyebrow text (e.g. "Friday Evening")
 *   hebrew       optional Hebrew name shown alongside title
 *   title        the big headline
 *   subtitle     italic sub-headline below title
 *   time         large display-serif time/date stamp
 *   image        optional image src
 *   body         array of paragraphs (string[])
 *   meta         array of { label, value } shown as a definition list
 *   cta          optional { label, onClick }
 */
export default function DetailModal({
  open,
  onClose,
  accent = 'var(--teal-500)',
  kicker,
  hebrew,
  title,
  subtitle,
  time,
  image,
  body = [],
  meta = [],
  cta,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#fff',
          borderRadius: 0,
          overflow: 'hidden',
          border: `1px solid rgba(36,34,88,0.16)`,
          boxShadow: '0 40px 100px -30px rgba(7,6,26,0.5)',
        },
      }}
      BackdropProps={{
        sx: { background: 'rgba(7,6,26,0.7)', backdropFilter: 'blur(4px)' },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* top accent bar */}
            <Box sx={{ height: '4px', background: accent }} />

            <Box sx={{ p: { xs: 3, md: 5 }, position: 'relative' }}>
              {/* close button */}
              <IconButton
                onClick={onClose}
                aria-label="Close"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  width: 36,
                  height: 36,
                  color: 'var(--navy-500)',
                  '&:hover': { background: 'rgba(36,34,88,0.06)' },
                }}
              >
                <Box sx={{ fontFamily: 'var(--font-display)', fontSize: 24, lineHeight: 1 }}>×</Box>
              </IconButton>

              {kicker && (
                <Box
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10.5,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    mb: 2,
                  }}
                >
                  {kicker}
                </Box>
              )}

              {/* Title row */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'baseline' }}
                spacing={{ xs: 1.5, md: 3 }}
                sx={{ mb: 2 }}
              >
                <Box
                  component="h2"
                  sx={{
                    margin: 0,
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'clamp(32px, 4.6vw, 60px)',
                    lineHeight: 1.0,
                    letterSpacing: '-0.015em',
                    color: 'var(--navy-500)',
                  }}
                >
                  {title}
                </Box>
                {hebrew && (
                  <Box
                    sx={{
                      fontFamily: 'var(--font-hebrew)',
                      fontSize: 'clamp(26px, 3vw, 40px)',
                      color: accent,
                      lineHeight: 1,
                      direction: 'rtl',
                    }}
                  >
                    {hebrew}
                  </Box>
                )}
              </Stack>

              {subtitle && (
                <Box
                  sx={{
                    fontFamily: 'var(--font-display)',
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 'clamp(18px, 1.8vw, 22px)',
                    color: 'var(--text-muted)',
                    lineHeight: 1.4,
                    mb: 3,
                  }}
                >
                  {subtitle}
                </Box>
              )}

              <Box sx={{ height: '1px', background: accent, opacity: 0.45, mb: 3 }} />

              {/* Time + image */}
              {(time || image) && (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 2, md: 4 }}
                  alignItems="flex-start"
                  sx={{ mb: 3 }}
                >
                  {time && (
                    <Box
                      sx={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 700,
                        fontSize: 'clamp(28px, 3.5vw, 44px)',
                        lineHeight: 1,
                        color: 'var(--navy-500)',
                        minWidth: { md: 200 },
                      }}
                    >
                      {time}
                    </Box>
                  )}
                  {image && (
                    <Box
                      component="img"
                      src={image}
                      alt=""
                      sx={{
                        width: '100%',
                        maxWidth: { md: 320 },
                        aspectRatio: '4/3',
                        objectFit: 'cover',
                        borderRadius: 0,
                        border: '1px solid rgba(36,34,88,0.12)',
                      }}
                    />
                  )}
                </Stack>
              )}

              {/* Body paragraphs */}
              {body.length > 0 && (
                <Stack spacing={2} sx={{ mb: 3 }}>
                  {body.map((p, i) => (
                    <Box
                      key={i}
                      sx={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 16,
                        lineHeight: 1.6,
                        color: 'var(--text)',
                      }}
                    >
                      {p}
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Meta definition list */}
              {meta.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                    mb: 3,
                    p: 2.5,
                    background: 'var(--cream-50)',
                    border: '1px solid rgba(36,34,88,0.08)',
                  }}
                >
                  {meta.map((m, i) => (
                    <Box key={i}>
                      <Box
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10,
                          letterSpacing: '0.22em',
                          textTransform: 'uppercase',
                          color: 'var(--text-muted)',
                          mb: 0.5,
                        }}
                      >
                        {m.label}
                      </Box>
                      <Box
                        sx={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 600,
                          fontSize: 17,
                          color: 'var(--navy-500)',
                          lineHeight: 1.3,
                        }}
                      >
                        {m.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* CTA */}
              {cta && (
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Box
                    onClick={cta.onClick}
                    sx={{
                      cursor: 'pointer',
                      px: 3.5,
                      py: 1.4,
                      background: accent,
                      color: '#fff',
                      fontFamily: 'var(--font-body)',
                      fontWeight: 700,
                      fontSize: 11.5,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      borderRadius: 999,
                      transition: 'transform 250ms ease, filter 250ms ease',
                      '&:hover': { transform: 'translateY(-2px)', filter: 'brightness(1.1)' },
                    }}
                  >
                    {cta.label}
                  </Box>
                </Stack>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
